const header = {
  init() {
    this.initToggle();
    this.initDrilldown();
    this.initMegaMenu();
    this.initScrollBehavior();
  },

  initScrollBehavior() {
    const el = document.querySelector('header');
    if (!el) return;

    let lastY = window.scrollY;
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const isNavOpen = document.body.classList.contains('nav--toggled');
        const isMegaOpen = document.getElementById('mega-menu')?.classList.contains('is-open');

        if (!isNavOpen && !isMegaOpen) {
          if (currentY > lastY && currentY > el.offsetHeight) {
            el.classList.add('header--hidden');
          } else {
            el.classList.remove('header--hidden');
          }
        }

        lastY = currentY;
        ticking = false;
      });
    }, { passive: true });
  },

  initToggle() {
    const navToggle = document.querySelector('.nav-toggle');
    const navWrapper = document.getElementById('nav-wrapper');
    if (!navToggle || !navWrapper) return;

    navToggle.addEventListener('click', () => {
      const isExpanded = navToggle.getAttribute('aria-expanded') === 'true';

      navToggle.setAttribute('aria-expanded', String(!isExpanded));
      navToggle.setAttribute('aria-label', isExpanded ? 'Open menu' : 'Close menu');
      navWrapper.classList.toggle('is-open', !isExpanded);

      if (isExpanded) {
        this.resetPanels();
      }
    });

    // close the mobile nav when crossing to desktop (e.g. tablet rotation), otherwise body stays scroll-locked
    window.matchMedia('(min-width: 1024px)').addEventListener('change', (e) => {
      if (e.matches && navToggle.getAttribute('aria-expanded') === 'true') {
        navToggle.click();
      }
    });
  },

  initDrilldown() {
    const panelsEl = document.getElementById('nav-panels');
    if (!panelsEl) return;

    const stack = ['root'];
    this._resetDrilldownStack = () => { stack.length = 0; stack.push('root'); };

    panelsEl.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-open-panel]');
      if (trigger) {
        const toId = trigger.dataset.openPanel;
        this.showPanel(panelsEl, stack[stack.length - 1], toId, 'forward');
        stack.push(toId);
        return;
      }

      const backBtn = e.target.closest('[data-back]');
      if (backBtn && stack.length > 1) {
        const fromId = stack.pop();
        this.showPanel(panelsEl, fromId, stack[stack.length - 1], 'back');
      }
    });
  },

  showPanel(panelsEl, fromId, toId, direction) {
    const fromPanel = panelsEl.querySelector(`[data-panel="${fromId}"]`);
    const toPanel = panelsEl.querySelector(`[data-panel="${toId}"]`);
    if (!fromPanel || !toPanel) return;

    if (direction === 'forward') {
      fromPanel.removeAttribute('data-active');
      fromPanel.setAttribute('data-behind', '');
      toPanel.setAttribute('data-active', '');
    } else {
      fromPanel.removeAttribute('data-active');
      toPanel.removeAttribute('data-behind');
      toPanel.setAttribute('data-active', '');
    }
  },

  resetPanels() {
    const panelsEl = document.getElementById('nav-panels');
    if (!panelsEl) return;

    panelsEl.querySelectorAll('[data-panel]').forEach((panel) => {
      panel.removeAttribute('data-active');
      panel.removeAttribute('data-behind');
    });

    const root = panelsEl.querySelector('[data-panel="root"]');
    if (root) root.setAttribute('data-active', '');

    if (this._resetDrilldownStack) this._resetDrilldownStack();
  },

  initMegaMenu() {
    const megaMenu = document.getElementById('mega-menu');
    if (!megaMenu) return;

    // trigger click — open matching panel, or close if already open
    document.querySelectorAll('[data-mega-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = trigger.dataset.megaTrigger;
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        this.closeMegaMenu();

        if (!isExpanded) {
          trigger.setAttribute('aria-expanded', 'true');
          megaMenu.classList.add('is-open');
          megaMenu.setAttribute('aria-hidden', 'false');

          const panel = megaMenu.querySelector(`[data-mega-panel="${id}"]`);
          if (panel) {
            panel.classList.add('is-active');
          }
        }
      });
    });

    // primary / secondary item click → show next column
    megaMenu.addEventListener('click', (e) => {
      const primaryTrigger = e.target.closest('[data-mega-primary]');
      if (primaryTrigger) {
        const panel = primaryTrigger.closest('[data-mega-panel]');
        if (panel) this.activatePrimaryItem(panel, primaryTrigger.dataset.megaPrimary);
        return;
      }

      const secondaryTrigger = e.target.closest('[data-mega-secondary-trigger]');
      if (secondaryTrigger) {
        const panel = secondaryTrigger.closest('[data-mega-panel]');
        if (panel) this.activateSecondaryItem(panel, secondaryTrigger.dataset.megaSecondaryTrigger);
      }
    });

    // close on outside click
    document.addEventListener('click', (e) => {
      if (
        megaMenu.classList.contains('is-open') &&
        !e.target.closest('[data-mega-trigger]') &&
        !e.target.closest('#mega-menu')
      ) {
        this.closeMegaMenu();
      }
    });
  },

  activatePrimaryItem(panel, id) {
    // deactivate all primary items
    panel.querySelectorAll('[data-mega-primary]').forEach((btn) => {
      btn.closest('.mega-menu__item').classList.remove('mega-menu__item--active');
      btn.setAttribute('aria-expanded', 'false');
    });

    // hide all secondary lists + reset tertiary
    panel.querySelectorAll('[data-mega-secondary]').forEach((list) => {
      list.classList.remove('is-active');
    });
    panel.querySelectorAll('[data-mega-secondary-trigger]').forEach((btn) => {
      btn.closest('.mega-menu__item').classList.remove('mega-menu__item--active');
      btn.setAttribute('aria-expanded', 'false');
    });
    panel.querySelectorAll('[data-mega-tertiary]').forEach((list) => {
      list.classList.remove('is-active');
    });

    // activate matching primary item
    const activeBtn = panel.querySelector(`[data-mega-primary="${id}"]`);
    if (activeBtn) {
      activeBtn.closest('.mega-menu__item').classList.add('mega-menu__item--active');
      activeBtn.setAttribute('aria-expanded', 'true');
    }

    // show matching secondary list
    const secondaryList = panel.querySelector(`[data-mega-secondary="${id}"]`);
    if (secondaryList) {
      secondaryList.classList.add('is-active');
    }
  },

  activateSecondaryItem(panel, id) {
    panel.querySelectorAll('[data-mega-secondary-trigger]').forEach((btn) => {
      btn.closest('.mega-menu__item').classList.remove('mega-menu__item--active');
      btn.setAttribute('aria-expanded', 'false');
    });
    panel.querySelectorAll('[data-mega-tertiary]').forEach((list) => {
      list.classList.remove('is-active');
    });

    const activeBtn = panel.querySelector(`[data-mega-secondary-trigger="${id}"]`);
    if (activeBtn) {
      activeBtn.closest('.mega-menu__item').classList.add('mega-menu__item--active');
      activeBtn.setAttribute('aria-expanded', 'true');
    }

    const tertiaryList = panel.querySelector(`[data-mega-tertiary="${id}"]`);
    if (tertiaryList) {
      tertiaryList.classList.add('is-active');
    }
  },

  closeMegaMenu() {
    const megaMenu = document.getElementById('mega-menu');
    if (!megaMenu) return;

    megaMenu.classList.remove('is-open');
    megaMenu.setAttribute('aria-hidden', 'true');

    document.querySelectorAll('[data-mega-trigger]').forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
    });

    megaMenu.querySelectorAll('[data-mega-panel]').forEach((panel) => {
      panel.classList.remove('is-active');
    });

    megaMenu.querySelectorAll('[data-mega-secondary],[data-mega-tertiary]').forEach((list) => {
      list.classList.remove('is-active');
    });

    megaMenu.querySelectorAll('[data-mega-primary],[data-mega-secondary-trigger]').forEach((btn) => {
      btn.closest('.mega-menu__item').classList.remove('mega-menu__item--active');
      btn.setAttribute('aria-expanded', 'false');
    });
  },
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => header.init());
} else {
  header.init();
}
